import React, { useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { sendNewsletterSignup } from '@/lib/dopplerService.js';

function NewsletterSection({ backgroundColor, backgroundImage }) {
  const [formData, setFormData] = useState({ nombre: '', email: '' });
  const [website, setWebsite] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    if (feedback) setFeedback(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (website) {
      setFeedback({ type: 'success', message: 'Gracias por suscribirte. Pronto vas a recibir novedades por email.' });
      return;
    }

    if (!formData.email) {
      const message = 'Ingresá tu email para suscribirte.';
      setFeedback({ type: 'error', message });
      toast.error(message);
      return;
    }

    if (!acceptedTerms) {
      const message = 'Necesitás aceptar las comunicaciones para continuar.';
      setFeedback({ type: 'error', message });
      toast.error(message);
      return;
    }

    setIsSubmitting(true);
    setFeedback(null);

    try {
      await sendNewsletterSignup(formData);
      const message = 'Gracias por suscribirte. Pronto vas a recibir novedades por email.';
      setFeedback({ type: 'success', message });
      toast.success(message);
      setFormData({ nombre: '', email: '' });
      setWebsite('');
      setAcceptedTerms(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No pudimos registrar tu suscripción. Intentá nuevamente.';
      setFeedback({ type: 'error', message });
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      className="bg-cover bg-center px-4 py-10 md:py-14"
      style={backgroundImage
        ? { backgroundImage: `linear-gradient(rgba(160, 68, 40, .62), rgba(160, 68, 40, .62)), url('${backgroundImage}')` }
        : backgroundColor
          ? { backgroundColor }
          : { backgroundImage: "url('/fondonewletterlunas.webp')" }}
    >
      <div className="mx-auto max-w-5xl sm:px-6 lg:px-8">
        <div className="newsletter-panel bg-white px-4 py-6 sm:px-8 sm:py-7 md:px-12 md:py-8">
          <div className="mx-auto mb-6 max-w-2xl text-center text-primary sm:mb-7">
            <p className="mx-auto mb-2 max-w-none text-xs font-semibold uppercase leading-snug tracking-[0.08em] text-primary/90 sm:text-sm">
              Sumate a mi newsletter para recibir
            </p>
            <h2 className="mb-2 text-3xl leading-none sm:text-4xl md:text-5xl">
              &quot;La ruta del sueño ideal&quot;
            </h2>
            <p className="mx-auto mb-3 max-w-lg text-sm font-bold uppercase leading-snug tracking-[0.04em] text-primary sm:text-base">
              Una guía con los pasos clave para mejorar el descanso de tu bebé
            </p>
            <p className="mx-auto max-w-2xl text-sm leading-relaxed text-foreground/85 sm:text-base">
              Además, te damos 10% OFF para tu primer curso y cada semana vas a recibir en tu mail tips, consejos y beneficios exclusivos.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="newsletter-form mx-auto max-w-3xl" noValidate>
            <div className="hidden" aria-hidden="true">
              <label htmlFor="newsletter-website">Sitio web</label>
              <input
                id="newsletter-website"
                name="website"
                type="text"
                value={website}
                onChange={(event) => setWebsite(event.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
              <div>
                <label htmlFor="newsletter-nombre" className="mb-2 block text-sm font-bold text-primary">
                  Nombre
                </label>
                <input
                  id="newsletter-nombre"
                  name="nombre"
                  type="text"
                  value={formData.nombre}
                  onChange={handleChange}
                  autoComplete="name"
                  disabled={isSubmitting}
                  className="newsletter-input"
                />
              </div>
              <div>
                <label htmlFor="newsletter-email" className="mb-2 block text-sm font-bold text-primary">
                  Email <span aria-hidden="true">*</span>
                </label>
                <input
                  id="newsletter-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                  inputMode="email"
                  required
                  disabled={isSubmitting}
                  className="newsletter-input"
                />
              </div>
            </div>

            <label className="mt-4 flex items-start gap-3 text-sm leading-relaxed text-foreground/85">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(event) => setAcceptedTerms(event.target.checked)}
                disabled={isSubmitting}
                className="mt-1 h-4 w-4 shrink-0 accent-[hsl(var(--primary))]"
              />
              <span>
                Acepto recibir comunicaciones por email y entiendo que puedo darme de baja cuando quiera.
              </span>
            </label>

            {feedback && (
              <p
                role="status"
                aria-live="polite"
                className={`mt-4 rounded-xl px-4 py-3 text-sm font-bold ${feedback.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}
              >
                {feedback.message}
              </p>
            )}

            <div className="mt-5 flex flex-col items-stretch gap-4 md:flex-row md:items-center md:justify-between">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-base min-h-12 bg-primary text-white disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isSubmitting ? 'Suscribiendo...' : 'Quiero recibirla'}
              </button>

              <p className="max-w-2xl text-sm leading-relaxed text-foreground/75">
                Al suscribirte aceptás nuestras{' '}
                <Link to="/privacidad" className="font-semibold text-primary underline underline-offset-4 hover:text-primary/80">
                  políticas de privacidad
                </Link>{' '}
                y los{' '}
                <Link to="/terminos-y-condiciones" className="font-semibold text-primary underline underline-offset-4 hover:text-primary/80">
                  términos y condiciones
                </Link>.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

export default NewsletterSection;
